import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  SquaresFour,
  UsersThree,
  UserList,
  Key,
  Microphone,
  Exam,
  Invoice,
  BookOpen,
  SignOut,
  List,
  X,
} from '@phosphor-icons/react';
import { useBranding } from '@/lib/branding/BrandingProvider';
import { useAuth } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/utils/cn';

const NAV = [
  { href: '/', label: 'Overview', icon: SquaresFour },
  { href: '/employees', label: 'Employees', icon: UsersThree, adminOnly: true },
  { href: '/end-clients', label: 'Candidates', icon: UserList },
  { href: '/interviews', label: 'Interviews', icon: Microphone },
  { href: '/tests', label: 'Tests', icon: Exam },
  { href: '/licenses', label: 'NeuraX Licenses', icon: Key },
  { href: '/billing', label: 'Billing', icon: Invoice },
  { href: '/guides', label: 'Guides', icon: BookOpen },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const brand = useBranding();
  const { employee, isAdmin, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative min-h-screen overflow-hidden bg-neurax-ink text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-neurax-glow opacity-70" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] grid-bg opacity-30" />

      <div className="relative flex min-h-screen">
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-72 transform border-r border-white/5 bg-neurax-midnight/80 backdrop-blur-xl transition-transform duration-300 lg:static lg:translate-x-0',
            open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          )}
        >
          <div className="flex h-full flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-12 w-auto max-w-[200px] object-contain self-start drop-shadow-[0_0_18px_rgba(91,124,255,0.35)]"
              />
              <p className="text-[11px] uppercase tracking-[0.22em] text-neurax-electric/80">
                {brand.productName}
              </p>
            </div>

            <nav className="flex flex-1 flex-col gap-1">
              {NAV.filter((n) => !n.adminOnly || isAdmin).map((item) => {
                const active =
                  location === item.href || (item.href !== '/' && location.startsWith(item.href));
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition',
                      active
                        ? 'bg-white/10 text-white shadow-glow-soft'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-4 w-4',
                        active
                          ? 'text-neurax-electric'
                          : 'text-muted-foreground group-hover:text-white'
                      )}
                    />
                    {item.label}
                    {active && (
                      <span className="absolute right-3 h-1.5 w-1.5 rounded-full bg-neurax-electric shadow-[0_0_12px_rgba(91,124,255,0.9)]" />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Signed in</p>
              <p className="mt-1 truncate text-sm font-medium text-white">
                {employee?.full_name || employee?.email}
              </p>
              <p className="text-xs text-muted-foreground">{employee?.email}</p>
              <button
                onClick={signOut}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-neurax-electric/10"
              >
                <SignOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </div>
          </div>
        </aside>

        <main className="flex min-h-screen flex-1 flex-col">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/5 bg-neurax-ink/70 px-5 py-4 backdrop-blur-xl lg:px-10">
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white"
            >
              {open ? <X className="h-4 w-4" /> : <List className="h-4 w-4" />}
            </button>
            <div className="flex items-center gap-3">
              <span className="hidden text-xs uppercase tracking-[0.22em] text-muted-foreground sm:inline">
                {brand.productName}
              </span>
              <span className="hidden h-3 w-px bg-white/10 sm:inline" />
              <span className="text-xs text-muted-foreground">
                Tenant · <span className="text-white">{brand.name}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-neurax-mint">
                Live
              </span>
            </div>
          </header>

          <div className="flex-1 px-5 py-8 lg:px-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
